import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, CssBaseline, Button, Paper,
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Skeleton,
  Collapse, IconButton, Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Header from '../Includes/Header';
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';
import { useSearchParams, useNavigate } from 'react-router-dom';

const HeadCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  fontWeight: 600,
  fontSize: 13,
}));

const BodyRow = styled(TableRow)(() => ({
  '&:nth-of-type(odd)': { backgroundColor: '#f9fafb' },
  '&:hover': { backgroundColor: 'rgba(27,67,50,0.05)' },
}));

const PunchRow = ({ punch, index, isLast }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: isLast ? 0 : 1 }}>
    {/* Timeline */}
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 0.4 }}>
      <Box sx={{
        width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
        bgcolor: punch.type === 'in' ? '#16a34a' : '#dc2626',
        boxShadow: `0 0 5px ${punch.type === 'in' ? '#16a34a55' : '#dc262655'}`,
      }} />
      {!isLast && <Box sx={{ width: 1.5, height: 20, bgcolor: '#e5e7eb', mt: 0.3 }} />}
    </Box>
    {/* Content */}
    <Box sx={{
      flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      bgcolor: punch.type === 'in' ? '#f0fdf4' : '#fff1f2',
      border: `1px solid ${punch.type === 'in' ? '#bbf7d0' : '#fecdd3'}`,
      borderRadius: 1.5, px: 1.5, py: 0.6,
    }}>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: punch.type === 'in' ? '#15803d' : '#b91c1c' }}>
        {punch.type === 'in' ? '↓ Entered' : '↑ Exited'}
      </Typography>
      <Typography sx={{ fontSize: 12, color: '#374151', fontWeight: 500 }}>
        {punch.time}
      </Typography>
    </Box>
  </Box>
);

const AttendanceRow = ({ row }) => {
  const { colors: appColors } = useAppTheme();
  const [open, setOpen] = useState(false);
  const hasPunches = Array.isArray(row.punches) && row.punches.length > 0;

  return (
    <>
      <BodyRow>
        <TableCell sx={{ width: 40, pr: 0 }}>
          {hasPunches && (
            <IconButton size="small" onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
            </IconButton>
          )}
        </TableCell>
        <TableCell sx={{ fontWeight: 600, color: appColors.evergreen, fontSize: 13 }}>{row.date}</TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#16a34a' }} />
            <Typography sx={{ fontSize: 13 }}>{row.check_in || '—'}</Typography>
          </Box>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: row.check_out ? '#dc2626' : '#d1d5db' }} />
            <Typography sx={{ fontSize: 13, color: row.check_out ? 'inherit' : '#9ca3af' }}>
              {row.check_out || 'Not yet'}
            </Typography>
          </Box>
        </TableCell>
        <TableCell>
          <Chip
            label={row.status || 'Present'}
            size="small"
            sx={{
              bgcolor: 'rgba(22,163,74,0.1)',
              color: '#15803d',
              fontWeight: 600,
              fontSize: 11,
            }}
          />
        </TableCell>
        <TableCell sx={{ fontSize: 13 }}>
          {row.work_hours != null ? `${row.work_hours} hrs` : '—'}
        </TableCell>
        <TableCell sx={{ fontSize: 12, color: '#6b7280' }}>
          {hasPunches ? `${row.total_checkins} check-in${row.total_checkins > 1 ? 's' : ''}` : '—'}
        </TableCell>
      </BodyRow>

      {/* Punch log expandable row */}
      {hasPunches && (
        <TableRow>
          <TableCell colSpan={7} sx={{ py: 0, bgcolor: '#f8fafc', borderBottom: open ? '1px solid #e5e7eb' : 'none' }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ py: 2, px: 3 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1, mb: 1.5 }}>
                  Full Activity — {row.punches.length} events on {row.date}
                </Typography>
                {row.punches.map((punch, pi) => (
                  <PunchRow key={pi} punch={punch} index={pi} isLast={pi === row.punches.length - 1} />
                ))}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const MonthlyAttendanceDetail = () => {
  const { colors: appColors } = useAppTheme();
  const [attendanceData, setAttendanceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const userId    = searchParams.get('userId');
  const userName  = searchParams.get('userName');
  const month     = searchParams.get('month');
  const monthName = searchParams.get('monthName');

  const fetchData = useCallback(async (pg, rpp) => {
    setLoading(true);
    const userData = JSON.parse(localStorage.getItem('userdata') || '{}');
    const userToken = userData.token;
    try {
      const response = await fetch(`${window.constants.api_url}/monthly-attendance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${userToken}` },
        body: JSON.stringify({ user_id: userId, month, limit: rpp, index: pg * rpp }),
      });
      const data = await response.json();
      setAttendanceData(Array.isArray(data.data) ? data.data : []);
      setTotalCount(data.total_count || 0);
    } catch (e) {
      console.error(e);
      setAttendanceData([]);
    } finally {
      setLoading(false);
    }
  }, [userId, month]);

  useEffect(() => { fetchData(page, rowsPerPage); }, [fetchData, page, rowsPerPage]);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f3f4f6' }}>
      <CssBaseline />
      <Box><Header /></Box>

      <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, sm: 3, md: 4 }, mt: { xs: 8, sm: 9 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ borderColor: appColors.evergreen, color: appColors.evergreen, '&:hover': { backgroundColor: 'rgba(27,67,50,0.08)' } }}
          >
            Back
          </Button>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: appColors.evergreen, textAlign: 'right' }}>
              {monthName} Attendance
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'right' }}>
              {userName}
            </Typography>
          </Box>
        </Box>

        {/* Table */}
        <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <HeadCell sx={{ width: 40 }} />
                  <HeadCell>Date</HeadCell>
                  <HeadCell>Check In</HeadCell>
                  <HeadCell>Check Out</HeadCell>
                  <HeadCell>Status</HeadCell>
                  <HeadCell>Work Hours</HeadCell>
                  <HeadCell>Total Check-ins</HeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading
                  ? Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 7 }).map((__, j) => (
                        <TableCell key={j}><Skeleton animation="wave" /></TableCell>
                      ))}
                    </TableRow>
                  ))
                  : attendanceData.length === 0
                    ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 6, color: '#9ca3af' }}>
                          No attendance records for {monthName}
                        </TableCell>
                      </TableRow>
                    )
                    : attendanceData.map((row, i) => <AttendanceRow key={i} row={row} />)
                }
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            sx={{
              borderTop: '1px solid #e5e7eb',
              '& .MuiTablePagination-toolbar': { justifyContent: 'center' },
              '& .MuiTablePagination-spacer': { display: 'none' },
            }}
          />
        </Paper>
      </Box>
    </Box>
  );
};

export default MonthlyAttendanceDetail;
